import { PlanPhaseTypeEnum } from '../../models/crm/types';

export enum AccountLifecycleEnum {
  /** No trading account at all - the welcome/upsell banner. */
  None = 'none',
  /**
   * An account is on its way: a pending account in the accounts call, a demo activation, or a plan
   * purchase callback.
   */
  Creating = 'creating',
  /**
   * The user's first ever Entry Funded account, waiting on a human reviewer. Split out of
   * `Creating` because the wait is measured in days rather than seconds, and it is the one state
   * with its own notification.
   */
  AwaitingApproval = 'awaitingApproval',
  /** The account exists but is not tradable yet - the first adjustment is not completed. */
  SettingUp = 'settingUp',
  /** The same, on a funded phase - the funded notification says it in its own words. */
  FundedSettingUp = 'fundedSettingUp',
  /** No plan left to trade on - a demo account, an expired demo, or every paid plan closed. */
  Upsell = 'upsell',
  /**
   * No notification applies: the account is ready, or a plan purchase is going through while the
   * demo account is still the selected one.
   */
  Ready = 'ready',
}

export type AccountLifecycleParams = {
  currentAccount?: string;
  hasActivePaidPlan: boolean;
  /** The current account is the first ever Entry Funded one and is waiting on approval. */
  isAwaitingApproval: boolean;
  hasPendingTradingAccount: boolean;
  isDemoAccountActivating: boolean;
  isFirstAdjustmentCompleted: boolean;
  hasBuyingPower: boolean;
  isFundedPhase: boolean;
  isPlanCallback: boolean;
  isUseDemoAccount: boolean;
};

export const isFundedPhase = (phase?: PlanPhaseTypeEnum): boolean => (
  phase === PlanPhaseTypeEnum.entry
  || phase === PlanPhaseTypeEnum.elite
  || phase === PlanPhaseTypeEnum.pro
);

/** The payments decide which notification applies, so neither is shown before they are loaded. */
export const isAwaitingPayments = (isPaymentsAllLoaded: boolean, currentAccount?: string): boolean => (
  !isPaymentsAllLoaded && !!currentAccount
);

type ActivePaidPlanParams = {
  hasNonClosedPlan: boolean;
  hasPaidPlan: boolean;
  isUseDemoAccount: boolean;
};

type DemoSetupParams = {
  hasDemoPlan: boolean;
  isDemoAccountActivating: boolean;
  isPendingAccountDemo: boolean;
  isUseDemoAccount: boolean;
};

/** Nothing to upsell once a paid plan is active. */
export const hasActivePaidPlan = ({
  hasNonClosedPlan,
  hasPaidPlan,
  isUseDemoAccount,
}: ActivePaidPlanParams): boolean => hasPaidPlan && (isUseDemoAccount || hasNonClosedPlan);

/** Which account the setup notification is talking about, so it uses the demo wording. */
export const isDemoAccountSettingUp = ({
  hasDemoPlan,
  isDemoAccountActivating,
  isPendingAccountDemo,
  isUseDemoAccount,
}: DemoSetupParams): boolean => (
  isDemoAccountActivating || isPendingAccountDemo || (hasDemoPlan && isUseDemoAccount)
);

export const getAccountLifecycle = ({
  currentAccount,
  hasActivePaidPlan: hasPaidPlanActive,
  isAwaitingApproval,
  hasPendingTradingAccount,
  isDemoAccountActivating,
  isFirstAdjustmentCompleted,
  hasBuyingPower,
  isFundedPhase: isFunded,
  isPlanCallback,
  isUseDemoAccount,
}: AccountLifecycleParams): AccountLifecycleEnum => {
  // The purchased account is not the selected one yet, so nothing is said until the switch happens.
  if (isPlanCallback && isUseDemoAccount) {
    return AccountLifecycleEnum.Ready;
  }

  // Checked before `Creating`: an account waiting on approval is also pending, but it is a wait of
  // days with its own notification rather than a creation that is nearly done.
  if (isAwaitingApproval) {
    return AccountLifecycleEnum.AwaitingApproval;
  }

  // The accounts call itself says so, which also covers a session that never saw the activation.
  if (hasPendingTradingAccount || isDemoAccountActivating || isPlanCallback) {
    return AccountLifecycleEnum.Creating;
  }

  if (!currentAccount) {
    return hasPaidPlanActive ? AccountLifecycleEnum.Ready : AccountLifecycleEnum.None;
  }

  const isSetUp = isFirstAdjustmentCompleted || hasBuyingPower;

  // A closed funded account has no plan left to trade on, so it is the upsell which applies rather
  // than the funded notification.
  if (isFunded) {
    if (!hasPaidPlanActive) {
      return AccountLifecycleEnum.Upsell;
    }

    return isSetUp ? AccountLifecycleEnum.Ready : AccountLifecycleEnum.FundedSettingUp;
  }

  if (!isSetUp) {
    return AccountLifecycleEnum.SettingUp;
  }

  return hasPaidPlanActive ? AccountLifecycleEnum.Ready : AccountLifecycleEnum.Upsell;
};
